import { TypedDocumentNode, gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react/hooks';

import { useConfigContext } from '@sorare/core/src/contexts/config';
import useFacetedSearchCards from '@sorare/core/src/hooks/search/useFacetedSearchCards';
import { joinFiltersWithAnd } from '@sorare/core/src/lib/algolia';

import {
  myLastLineupQuery,
  myLastLineupQueryVariables,
} from './__generated__/useFavPlayer.graphql';

export const LAST_LINEUP_QUERY = gql`
  query myLastLineupQuery {
    so5 {
      myUpcomingLineupsPaginated {
        nodes {
          id
          name
          draft
          so5Appearances {
            id
            captain
            anyCard {
              slug
              anyPlayer {
                slug
                displayName
              }
            }
          }
        }
        totalCount
      }
    }
  }
` as TypedDocumentNode<myLastLineupQuery, myLastLineupQueryVariables>;

type PlayerHit = {
  player: {
    display_name: string;
  };
};
const ATTRIBUTES_TO_RETRIEVE = ['sale', 'player', 'rarity', 'season', 'slug'];

const useFavPlayer = ({ skip = false }: { skip?: boolean }) => {
  const { algoliaCardIndexes } = useConfigContext();

  const { data } = useQuery(LAST_LINEUP_QUERY, { skip });

  const card =
    data?.so5.myUpcomingLineupsPaginated.nodes[0]?.so5Appearances.find(
      so5Appearance => so5Appearance.captain
    )?.anyCard;

  const { results: myCaptainHits } = useFacetedSearchCards<PlayerHit>({
    attributesToRetrieve: ATTRIBUTES_TO_RETRIEVE,
    index: algoliaCardIndexes['Cards On Sale Popular Player'],
    distinct: true,
    facetFilters: joinFiltersWithAnd([
      'sport:football',
      `player.slug:${card?.anyPlayer.slug}`,
      'sale.primary:false',
      'sale.bundled:false',
    ]),
    hitsPerPage: 1,
    skip: !card?.anyPlayer.slug || skip,
  });

  const { results: popularPlayer } = useFacetedSearchCards<PlayerHit>({
    attributesToRetrieve: ATTRIBUTES_TO_RETRIEVE,
    index: algoliaCardIndexes['Cards On Sale Popular Player'],
    distinct: true,
    facetFilters: joinFiltersWithAnd([
      'sport:football',
      'sale.primary:false',
      'sale.bundled:false',
    ]),
    hitsPerPage: 1,
    skip,
  });
  return myCaptainHits?.hits[0]?.player || popularPlayer?.hits[0]?.player;
};

export default useFavPlayer;
